import { useCallback, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ColumnDef, PaginationState, SortingState } from '@tanstack/react-table';
import { DataGrid } from '@/components/DataGrid/DataGrid';
import { useAuth } from '@/auth/useAuth';
import { Permission } from '@/auth/permissions';
import { SpriteIcon } from '@/components/SpriteIcon';
import { AuditLogDetailsModal } from './AuditLogDetailsModal';
import {
  downloadAuditLogCsv,
  operationTypeNames,
  operationTypeTone,
  useAuditLogObjectTypes,
  useAuditLogOwners,
  useAuditLogSearch,
  useDeleteOldAuditLogs,
  type AuditLogFilter,
  type AuditLogListItem,
} from './useAuditLogs';

/**
 * Replaces `Pages/Admin/AuditLogs.cshtml` at `/admin/audit-logs`.
 *
 * The filters are applied on submit rather than as you type, which is what the
 * Razor form did — this is a query over a table that grows without bound, and
 * re-running it on every keystroke would be a behaviour change, not a nicety.
 */

/** Columns the API can order by; anything else it ignores. */
const SORTABLE = new Set(['operationDate', 'owner', 'objectType', 'operationType', 'isSuccess']);

interface FilterForm {
  dateFrom: string;
  dateTo: string;
  operationType: string;
  objectType: string;
  owner: string;
  ipAddress: string;
  isSuccess: string;
  searchTerm: string;
}

const EMPTY_FILTERS: FilterForm = {
  dateFrom: '',
  dateTo: '',
  operationType: '',
  objectType: '',
  owner: '',
  ipAddress: '',
  isSuccess: '',
  searchTerm: '',
};

function formatDateTime(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

export function AuditLogsPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  const can = {
    export: hasPermission(Permission.auditLogs.export),
    delete: hasPermission(Permission.auditLogs.delete),
  };

  // Two copies: what the form shows, and what the last Search applied. The grid
  // reads the applied set so editing a field does not re-query until submitted.
  const [form, setForm] = useState<FilterForm>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<FilterForm>(EMPTY_FILTERS);

  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'operationDate', desc: true }]);
  const [viewing, setViewing] = useState<number | null>(null);
  const [deleteOldOpen, setDeleteOldOpen] = useState(false);
  const [olderThan, setOlderThan] = useState('');
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const { data: objectTypes } = useAuditLogObjectTypes();
  const { data: owners } = useAuditLogOwners();
  const deleteOld = useDeleteOldAuditLogs();

  const filter = useMemo<AuditLogFilter>(() => {
    const sort = sorting[0];
    return {
      dateFrom: applied.dateFrom || null,
      dateTo: applied.dateTo || null,
      operationType: applied.operationType ? Number(applied.operationType) : null,
      objectType: applied.objectType || null,
      owner: applied.owner || null,
      ipAddress: applied.ipAddress || null,
      isSuccess: applied.isSuccess === '' ? null : applied.isSuccess === 'true',
      searchTerm: applied.searchTerm || null,
      pageNumber: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy: sort && SORTABLE.has(sort.id) ? sort.id : 'operationDate',
      sortDirection: sort?.desc === false ? 'asc' : 'desc',
    };
  }, [applied, pagination, sorting]);

  const { data, isLoading, isFetching } = useAuditLogSearch(filter);

  const setField = useCallback(
    (key: keyof FilterForm, value: string) => setForm((f) => ({ ...f, [key]: value })),
    [],
  );

  const handleSearch = useCallback(() => {
    setApplied(form);
    // The page number belongs to the old result set.
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [form]);

  const handleClear = useCallback(() => {
    setForm(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadAuditLogCsv(filter);
    } catch {
      setNotice({ tone: 'danger', text: t('AuditLogs.ExportError', 'Could not export the logs.') });
    } finally {
      setExporting(false);
    }
  }, [exporting, filter, t]);

  const handleDeleteOld = useCallback(() => {
    if (!olderThan) return;
    deleteOld.mutate(olderThan, {
      onSuccess: (removed) => {
        setDeleteOldOpen(false);
        setOlderThan('');
        setNotice({
          tone: 'success',
          text: t('AuditLogs.Deleted', '{{count}} log entries deleted.', { count: removed ?? 0 }),
        });
      },
      onError: () =>
        setNotice({ tone: 'danger', text: t('AuditLogs.DeleteError', 'Could not delete the logs.') }),
    });
  }, [olderThan, deleteOld, t]);

  const columns = useMemo<ColumnDef<AuditLogListItem, unknown>[]>(
    () => [
      {
        id: 'operationDate',
        accessorKey: 'operationDate',
        header: t('AuditLogs.Date', 'Date'),
        cell: ({ row }) => formatDateTime(row.original.operationDate),
      },
      { id: 'owner', accessorKey: 'owner', header: t('AuditLogs.Owner', 'Owner') },
      { id: 'objectType', accessorKey: 'objectType', header: t('AuditLogs.ObjectType', 'Object type') },
      {
        id: 'objectId',
        accessorKey: 'objectId',
        header: t('AuditLogs.ObjectId', 'Object ID'),
        enableSorting: false,
        cell: ({ row }) => row.original.objectId || '—',
      },
      {
        id: 'operationType',
        accessorKey: 'operationType',
        header: t('AuditLogs.Operation', 'Operation'),
        meta: { align: 'center' },
        cell: ({ row }) => {
          const value = row.original.operationType;
          return (
            <span className={`badge ${operationTypeTone[value] ?? 'bg-secondary'}`}>
              {operationTypeNames[value] ?? value}
            </span>
          );
        },
      },
      {
        id: 'ipAddress',
        accessorKey: 'ipAddress',
        header: t('AuditLogs.IpAddress', 'IP address'),
        enableSorting: false,
      },
      {
        id: 'isSuccess',
        accessorKey: 'isSuccess',
        header: t('AuditLogs.Status', 'Status'),
        meta: { align: 'center' },
        cell: ({ row }) =>
          row.original.isSuccess ? (
            <span className="badge bg-success">{t('AuditLogs.Success', 'Success')}</span>
          ) : (
            <span className="badge bg-danger" title={row.original.errorMessage ?? undefined}>
              {t('AuditLogs.Failed', 'Failed')}
            </span>
          ),
      },
      {
        id: 'actions',
        header: () => null,
        enableSorting: false,
        meta: { align: 'end' },
        cell: ({ row }) => (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => setViewing(row.original.id)}
            title={t('AuditLogs.ViewDetails', 'View details')}
            aria-label={t('AuditLogs.ViewDetails', 'View details')}
          >
            <SpriteIcon name="eye" size={14} />
          </button>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="container-fluid py-3">
      <div className="mb-3">
        <h1 className="h3 mb-1">{t('AuditLogs.Title', 'Audit logs')}</h1>
        <p className="text-muted mb-0">
          {t('AuditLogs.Subtitle', 'Operation history for audit and compliance.')}
        </p>
      </div>

      {notice && (
        <div className={`alert alert-${notice.tone} alert-dismissible`} role="alert">
          {notice.text}
          <button type="button" className="btn-close" onClick={() => setNotice(null)} />
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-transparent">
          <h5 className="mb-0">{t('AuditLogs.Filters', 'Filters')}</h5>
        </div>
        <div className="card-body">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSearch();
            }}
          >
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="af-from">
                  {t('AuditLogs.DateFrom', 'Date from')}
                </label>
                <input
                  id="af-from"
                  type="date"
                  className="form-control"
                  value={form.dateFrom}
                  onChange={(e) => setField('dateFrom', e.target.value)}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="af-to">
                  {t('AuditLogs.DateTo', 'Date to')}
                </label>
                <input
                  id="af-to"
                  type="date"
                  className="form-control"
                  value={form.dateTo}
                  onChange={(e) => setField('dateTo', e.target.value)}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="af-op">
                  {t('AuditLogs.Operation', 'Operation')}
                </label>
                <select
                  id="af-op"
                  className="form-select"
                  value={form.operationType}
                  onChange={(e) => setField('operationType', e.target.value)}
                >
                  <option value="">{t('AuditLogs.AllOperations', 'All operations')}</option>
                  {Object.entries(operationTypeNames).map(([value, name]) => (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="af-objtype">
                  {t('AuditLogs.ObjectType', 'Object type')}
                </label>
                <select
                  id="af-objtype"
                  className="form-select"
                  value={form.objectType}
                  onChange={(e) => setField('objectType', e.target.value)}
                >
                  <option value="">{t('AuditLogs.AllTypes', 'All types')}</option>
                  {(objectTypes ?? []).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="af-owner">
                  {t('AuditLogs.Owner', 'Owner')}
                </label>
                <select
                  id="af-owner"
                  className="form-select"
                  value={form.owner}
                  onChange={(e) => setField('owner', e.target.value)}
                >
                  <option value="">{t('AuditLogs.AllUsers', 'All users')}</option>
                  {(owners ?? []).map((owner) => (
                    <option key={owner} value={owner}>
                      {owner}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="af-ip">
                  {t('AuditLogs.IpAddress', 'IP address')}
                </label>
                <input
                  id="af-ip"
                  className="form-control"
                  value={form.ipAddress}
                  onChange={(e) => setField('ipAddress', e.target.value)}
                  placeholder="e.g. 192.168.1.1"
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="af-status">
                  {t('AuditLogs.Status', 'Status')}
                </label>
                <select
                  id="af-status"
                  className="form-select"
                  value={form.isSuccess}
                  onChange={(e) => setField('isSuccess', e.target.value)}
                >
                  <option value="">{t('AuditLogs.All', 'All')}</option>
                  <option value="true">{t('AuditLogs.Success', 'Success')}</option>
                  <option value="false">{t('AuditLogs.Failed', 'Failed')}</option>
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="af-search">
                  {t('AuditLogs.Search', 'Search')}
                </label>
                <input
                  id="af-search"
                  className="form-control"
                  value={form.searchTerm}
                  onChange={(e) => setField('searchTerm', e.target.value)}
                  placeholder={t('AuditLogs.SearchPlaceholder', 'Search in details…')}
                />
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button type="submit" className="btn btn-primary">
                {t('AuditLogs.SearchButton', 'Search')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleClear}>
                {t('AuditLogs.ClearFilters', 'Clear filters')}
              </button>
              {can.export && (
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleExport}
                  disabled={exporting}
                >
                  {exporting
                    ? t('AuditLogs.Exporting', 'Exporting…')
                    : t('AuditLogs.ExportCsv', 'Export CSV')}
                </button>
              )}
              {can.delete && (
                <button
                  type="button"
                  className="btn btn-danger ms-auto"
                  onClick={() => setDeleteOldOpen(true)}
                >
                  {t('AuditLogs.DeleteOld', 'Delete old logs')}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header bg-transparent d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{t('AuditLogs.Entries', 'Entries')}</h5>
          <span className="badge bg-primary">
            {t('AuditLogs.Count', '{{count}} logs', { count: data?.totalCount ?? 0 })}
          </span>
        </div>
        <div className="card-body">
          <DataGrid<AuditLogListItem>
            columns={columns}
            rows={data?.items ?? []}
            total={data?.totalCount ?? 0}
            pagination={pagination}
            onPaginationChange={setPagination}
            sorting={sorting}
            onSortingChange={setSorting}
            isLoading={isLoading}
            isFetching={isFetching}
            getRowId={(row) => String(row.id)}
            pageSizeOptions={[10, 25, 50, 100]}
            emptyMessage={t('AuditLogs.Empty', 'No log entries match these filters')}
          />
        </div>
      </div>

      {viewing != null && (
        <AuditLogDetailsModal logId={viewing} onClose={() => setViewing(null)} />
      )}

      {deleteOldOpen && (
        <Modal show onHide={() => setDeleteOldOpen(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title as="h5">{t('AuditLogs.DeleteOld', 'Delete old logs')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              {t(
                'AuditLogs.DeleteOldWarning',
                'Every log entry older than this date will be permanently deleted. This cannot be undone.',
              )}
            </p>
            <label className="form-label" htmlFor="af-older-than">
              {t('AuditLogs.OlderThan', 'Delete entries older than')}
            </label>
            <input
              id="af-older-than"
              type="date"
              className="form-control"
              value={olderThan}
              onChange={(e) => setOlderThan(e.target.value)}
            />
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setDeleteOldOpen(false)}
            >
              {t('Cancel', 'Cancel')}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDeleteOld}
              // No date means "older than the epoch default", which would match
              // everything — the server takes whatever it is sent.
              disabled={!olderThan || deleteOld.isPending}
            >
              {deleteOld.isPending ? t('Deleting', 'Deleting…') : t('Delete', 'Delete')}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
